import { FC, useEffect, useState } from 'react';
import { CookiesProvider } from 'react-cookie';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider, QueryMeta } from '@tanstack/react-query';
import ThemeProvider from '@components/features/themeProvider/ThemeProvider';
import { MatomoProvider } from '@datapunt/matomo-tracker-react';
import { matomoInstance } from './matomo-init';
import Root from '@js/Root';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { MsalProvider } from '@azure/msal-react';
import { toast } from 'react-toastify';
import { PublicClientApplication } from '@azure/msal-browser';
import { msalConfig } from '@js/authConfig';
import { QueryError } from '@models/QueryError';

const msalInstance = new PublicClientApplication(msalConfig);

const Hubl: FC = () => {
    const [isMsalInitialized, setIsMsalInitialized] = useState(false);

    useEffect(() => {
        msalInstance
            .initialize()
            .then(() => {
                setIsMsalInitialized(true);
            })
            .catch((error) => {
                console.error('MSAL initialization failed', error);
            });
    }, []);

    const onGlobalQueryError = (error: QueryError, meta?: QueryMeta) => {
        if (meta?.disableErrorToaster) {
            return;
        }

        const errorMessage = error?.response?.data?.message || error?.response?.data?.Message;
        if (errorMessage) {
            toast.error(errorMessage);
        }
    };

    const queryClient = new QueryClient({
        queryCache: new QueryCache({
            onError: (error, query) => onGlobalQueryError(error as QueryError, query.meta),
        }),
        mutationCache: new MutationCache({
            onError: (error, variables, context, mutation) => onGlobalQueryError(error as QueryError, mutation.options.meta),
        }),
        defaultOptions: {
            mutations: {
                retry: 3,
                retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
            },
        },
    });

    if (!isMsalInitialized) {
        return null;
    }

    return (
        <MsalProvider instance={msalInstance}>
            <CookiesProvider>
                <DndProvider backend={HTML5Backend}>
                    <QueryClientProvider client={queryClient}>
                        <ThemeProvider>
                            {/* @ts-expect-error This is an error in the package */}
                            <MatomoProvider value={matomoInstance}>
                                <Root />
                            </MatomoProvider>
                        </ThemeProvider>
                        <ReactQueryDevtools initialIsOpen={false} />
                    </QueryClientProvider>
                </DndProvider>
            </CookiesProvider>
        </MsalProvider>
    );
};

export default Hubl;
